/**
 * Legacy info.txt (RPE's manifest) and the key mapping it shares with info.csv (W1-07b formats-package; contract
 * formats/info/types.ts `ReadInfoTxtFn`, `WriteInfoTxtFn`; formats-and-packaging.md P§2.4).
 *
 * Reading, exactly as Phira:
 *  - lines split like Rust `lines()` (on "\n", one trailing "\r" removed per line); lines that are exactly empty are
 *    dropped, a line of spaces is not;
 *  - the first remaining line must be exactly "#" ('txtHeader' otherwise; read on leniently);
 *  - every other line must contain ": "; the key is the text before the FIRST ": " (trimmed), the value everything
 *    after it, untrimmed ('badTxtLine' for a line without it; skipped);
 *  - keys are case-sensitive: Name; Music / Song; Chart; Image / Picture; Level (also sets the difficulty from the
 *    trailing ASCII digits: "IN Lv.15" -> 15, "Lv.?" keeps 10, "15.5" -> 5, the legacy quirk, import only);
 *    Illustrator; Artist / Composer / Musician; Charter / Designer; AspectRatio and BackgroundDim (Rust f32 parsing:
 *    any other text, trailing spaces included, is 'txtParseFloat'); NoteScale / ScaleRatio / GlobalAlpha and every
 *    other key are ignored.
 * Every diagnostic is fatal for Phira (it aborts the import on the first manifest error).
 *
 * Writing produces RPE's layout: "#", then Name, Path (the RPE chart id), Song, Picture, Chart, Level, Composer,
 * Charter, one "Key: Value" per line, line breaks inside values replaced by spaces, "\n" line ends.
 */

import { defaultInfo } from './infoYml';
import type { InfoTxtFields, InfoYml, ManifestDiagnostic, ReadInfoTxtFn, WriteInfoTxtFn } from './types';

type MutableInfo = { -readonly [K in keyof InfoYml]: InfoYml[K] };

const BOM = String.fromCharCode(0xfeff);
/** CR LF, CR, LF, NEL, LINE SEPARATOR, PARAGRAPH SEPARATOR. */
const LINE_BREAKS_RE = new RegExp('\\r\\n|[\\r\\n\\x85\\u2028\\u2029]', 'g');

/** Rust `str::parse::<f32>` grammar (no surrounding spaces; inf / infinity / nan in any case). */
const RUST_F32_RE = /^[+-]?(?:inf|infinity|nan|(?:[0-9]+\.?[0-9]*|\.[0-9]+)(?:[eE][+-]?[0-9]+)?)$/i;

/** Rust's f32 parse of a string, or null when Rust would return an error. */
export function parseRustF32(s: string): number | null {
  if (!RUST_F32_RE.test(s)) return null;
  const lower = s.toLowerCase();
  const neg = lower.startsWith('-');
  const body = lower.replace(/^[+-]/, '');
  if (body === 'nan') return Number.NaN;
  if (body === 'inf' || body === 'infinity') return neg ? -Infinity : Infinity;
  return Math.fround(Number(s));
}

/**
 * Difficulty from the trailing run of ASCII digits of a level string (Phira's legacy quirk): "IN Lv.15" -> 15,
 * "15.5" -> 5; null when the level does not end in a digit (the difficulty is then left unchanged).
 */
export function difficultyFromLevel(level: string): number | null {
  const m = /[0-9]+$/.exec(level);
  if (m === null) return null;
  return Math.fround(Number(m[0]));
}

/** Applies one manifest key of info.txt / info.csv to `info` (P§2.4 key table). */
export function applyLegacyKey(info: MutableInfo, key: string, value: string, diags: ManifestDiagnostic[]): void {
  switch (key) {
    case 'Name':
      info.name = value;
      return;
    case 'Music':
    case 'Song':
      info.music = value;
      return;
    case 'Chart':
      info.chart = value;
      return;
    case 'Image':
    case 'Picture':
      info.illustration = value;
      return;
    case 'Level': {
      info.level = value;
      const d = difficultyFromLevel(value);
      if (d !== null) info.difficulty = d;
      return;
    }
    case 'Illustrator':
      info.illustrator = value;
      return;
    case 'Artist':
    case 'Composer':
    case 'Musician':
      info.composer = value;
      return;
    case 'Charter':
    case 'Designer':
      info.charter = value;
      return;
    case 'AspectRatio':
    case 'BackgroundDim': {
      const n = parseRustF32(value);
      if (n === null) {
        diags.push({ code: 'txtParseFloat', detail: `${key}: ${value}`, phiraFatal: true });
        return;
      }
      if (key === 'AspectRatio') info.aspectRatio = n;
      else info.backgroundDim = n;
      return;
    }
    default:
      // NoteScale, ScaleRatio, GlobalAlpha (Phira warns), Path, LastEditTime, Length, EditTime, ...: ignored.
      return;
  }
}

/** Rust `str::lines()`: split on "\n", strip one trailing "\r" per line, no final empty line. */
export function rustLines(text: string): string[] {
  if (text.length === 0) return [];
  const parts = text.split('\n');
  if (parts[parts.length - 1] === '') parts.pop();
  return parts.map((l) => (l.endsWith('\r') ? l.slice(0, -1) : l));
}

export const readInfoTxt: ReadInfoTxtFn = (text) => {
  const info = defaultInfo() as MutableInfo;
  const diagnostics: ManifestDiagnostic[] = [];
  const lines = rustLines(text).filter((l) => l !== '');
  const first = lines[0];
  let start = 1;
  if (first !== '#' && first !== `${BOM}#`) {
    diagnostics.push({ code: 'txtHeader', detail: first ?? '', phiraFatal: true });
    // Lenient: a first line that is already a "Key: Value" pair is read as one.
    if (first?.includes(': ') === true) start = 0;
  }
  for (const line of lines.slice(start)) {
    const at = line.indexOf(': ');
    if (at < 0) {
      diagnostics.push({ code: 'badTxtLine', detail: line, phiraFatal: true });
      continue;
    }
    applyLegacyKey(info, line.slice(0, at).trim(), line.slice(at + 2), diagnostics);
  }
  return { info, diagnostics };
};

/** One info.txt value: line breaks become spaces (a value must stay on its line). */
function txtValue(v: string): string {
  return v.replace(LINE_BREAKS_RE, ' ');
}

/** RPE's key order. */
const TXT_KEYS: readonly (keyof InfoTxtFields)[] = [
  'Name',
  'Path',
  'Song',
  'Picture',
  'Chart',
  'Level',
  'Composer',
  'Charter',
];

export const writeInfoTxt: WriteInfoTxtFn = (f) => `#\n${TXT_KEYS.map((k) => `${k}: ${txtValue(f[k])}\n`).join('')}`;
